import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  farmaciaSchema,
  parseFarmacia,
  isValidSlug,
  type Farmacia,
  type Foto,
  type Logo,
} from "@farmacias/schema";
import { db, storage } from "./firebase";

const COL = "farmacias";

function nowIso(): string {
  return new Date().toISOString();
}

/** Tenta parsear; se o doc estiver fora do schema, cai pra um doc só com defaults. */
function coerce(slug: string, data: Record<string, unknown>): Farmacia {
  const parsed = farmaciaSchema.safeParse({ ...data, slug });
  if (parsed.success) return parsed.data;
  console.warn(`farmacia ${slug}: doc não bate no schema`, parsed.error.issues);
  return farmaciaSchema.parse({ slug });
}

export async function listFarmacias(): Promise<Farmacia[]> {
  const snap = await getDocs(query(collection(db, COL), orderBy("nomeFantasia")));
  return snap.docs.map((d) => coerce(d.id, d.data()));
}

export async function getFarmacia(slug: string): Promise<Farmacia | null> {
  const snap = await getDoc(doc(db, COL, slug));
  return snap.exists() ? coerce(slug, snap.data()) : null;
}

export async function slugExists(slug: string): Promise<boolean> {
  const snap = await getDoc(doc(db, COL, slug));
  return snap.exists();
}

/** Cria um rascunho novo. Falha se o slug for inválido/reservado ou já existir. */
export async function createFarmacia(slug: string, data: Partial<Farmacia>): Promise<Farmacia> {
  if (!isValidSlug(slug)) throw new Error("Slug inválido ou reservado.");
  if (await slugExists(slug)) throw new Error("Já existe uma farmácia com esse slug.");
  const iso = nowIso();
  const full = parseFarmacia({ ...data, slug, status: "draft", createdAt: iso, updatedAt: iso });
  await setDoc(doc(db, COL, slug), full);
  return full;
}

/**
 * Campos de propriedade da painel-farmacia-api (fluxo de domínio próprio). O
 * painel nunca os escreve — relê o valor do servidor antes de qualquer `setDoc`
 * de documento inteiro pra não sobrescrever o que a API gravou.
 */
const API_OWNED = ["customDomain", "customDomainStatus"] as const;

/** Salva alterações de um rascunho/publicado (não mexe em status). */
export async function saveFarmacia(current: Farmacia, patch: Partial<Farmacia>): Promise<Farmacia> {
  const fresh = await getDoc(doc(db, COL, current.slug));
  const server = (fresh.exists() ? fresh.data() : {}) as Record<string, unknown>;
  const apiOwned = Object.fromEntries(API_OWNED.map((k) => [k, server[k] ?? current[k]]));
  const next = parseFarmacia({
    ...current,
    ...patch,
    ...apiOwned,
    slug: current.slug,
    status: current.status,
    createdAt: current.createdAt ?? nowIso(),
    updatedAt: nowIso(),
  });
  await setDoc(doc(db, COL, current.slug), next);
  return next;
}

export async function setStatus(slug: string, status: "draft" | "published"): Promise<void> {
  const snap = await getDoc(doc(db, COL, slug));
  if (!snap.exists()) throw new Error("Farmácia não encontrada.");
  const next = parseFarmacia({ ...snap.data(), slug, status, updatedAt: nowIso() });
  await setDoc(doc(db, COL, slug), next);
}

export async function deleteFarmacia(slug: string): Promise<void> {
  await deleteDoc(doc(db, COL, slug));
}

const LOGO_TYPES = new Set(["image/png", "image/svg+xml", "image/jpeg"]);
const FOTO_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MB = 1024 * 1024;

/** Sobe o arquivo e devolve `{ path, url }`. As regras do bucket repetem os limites. */
async function upload(path: string, file: File): Promise<{ path: string; url: string }> {
  const r = ref(storage, path);
  await uploadBytes(r, file, { contentType: file.type });
  return { path, url: await getDownloadURL(r) };
}

function extensao(file: File, fallback: string): string {
  return file.name.split(".").pop()?.toLowerCase() || fallback;
}

export async function uploadLogo(slug: string, file: File): Promise<Logo> {
  if (!LOGO_TYPES.has(file.type)) throw new Error("Logo deve ser PNG, SVG ou JPEG.");
  // `>=` e não `>`: as storage.rules exigem `size < 5MB`, então o arquivo de
  // exatamente 5 MB passava aqui e morria lá como `storage/unauthorized`.
  if (file.size >= 5 * MB) throw new Error("Logo deve ter menos de 5 MB.");
  const { path, url } = await upload(
    `logos/${slug}/logo-${Date.now()}.${extensao(file, "png")}`,
    file,
  );
  return { path, url, contentType: file.type };
}

/** Fotos da landing (hero e fachada). `alt` é preenchido depois, no formulário. */
export async function uploadFoto(
  slug: string,
  tipo: "hero" | "fachada",
  file: File,
): Promise<Foto> {
  if (!FOTO_TYPES.has(file.type)) throw new Error("Foto deve ser JPEG, PNG ou WEBP.");
  if (file.size >= 8 * MB) throw new Error("Foto deve ter menos de 8 MB.");
  const { path, url } = await upload(
    `fotos/${slug}/${tipo}-${Date.now()}.${extensao(file, "jpg")}`,
    file,
  );
  return { path, url, contentType: file.type, alt: "" };
}
